/**
 * Status chips — the four tones.
 *
 * Every status answers "is this on me or on them?", so the palette collapses to:
 *   neutral  Draft / not requested yet            (inset, shape dot)
 *   held     With them · N days left               (inset, shape dot)
 *   needs    Needs you                             (amber-wash, amber dot)
 *   breach   Past deadline · N days / With the ICO (breach-wash, breach dot)
 *   done     Records received                      (brand-wash, brand dot)
 *
 * Rules: dot + word always (never colour alone); a number whenever a clock is
 * running; no icons — they only ever repeated the label.
 */

export const TONES = {
  neutral: { chip: 'bg-inset text-ink-70', dot: 'bg-shape' },
  held:    { chip: 'bg-inset text-ink-70', dot: 'bg-shape' },
  needs:   { chip: 'bg-amber-wash text-amber-ink', dot: 'bg-amber' },
  breach:  { chip: 'bg-breach-wash text-breach-ink', dot: 'bg-breach' },
  done:    { chip: 'bg-brand-wash text-brand-ink', dot: 'bg-brand' },
};

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** "With them · 14 days left" / "Past deadline · 9 days" depending on the clock. */
function withDays(base, days) {
  if (typeof days !== 'number' || Number.isNaN(days)) return base;
  if (days < 0) return `Past deadline · ${plural(Math.abs(days), 'day')}`;
  return `${base} · ${plural(days, 'day')} left`;
}

/** SAR status -> tone + label(days). `days` is days remaining (negative = overdue). */
export const SAR_TONES = {
  draft:             { tone: 'neutral', label: () => 'Draft' },
  sent:              { tone: 'held',    label: (d) => withDays('With them', d) },
  awaiting_response: { tone: 'held',    label: (d) => withDays('With them', d) },
  partial:           { tone: 'held',    label: (d) => withDays('Part sent', d) },
  overdue:           { tone: 'breach',  label: (d) => `Past deadline${typeof d === 'number' ? ` · ${plural(Math.abs(d), 'day')}` : ''}` },
  escalated_ico:     { tone: 'breach',  label: () => 'With the ICO' },
  escalated:         { tone: 'breach',  label: () => 'With the ICO' },
  refusal:           { tone: 'breach',  label: () => 'Refused' },
  refused:           { tone: 'breach',  label: () => 'Refused' },
  needs_you:         { tone: 'needs',   label: () => 'Needs you' },
  complete:          { tone: 'done',    label: () => 'Records received' },
};

/** Organisation status -> tone + label(days). */
export const ORG_TONES = {
  ready:             { tone: 'neutral', label: () => 'Not requested yet' },
  needs_info:        { tone: 'needs',   label: () => 'Needs you' },
  unresolved:        { tone: 'needs',   label: () => 'Which council?' },
  has_issues:        { tone: 'needs',   label: () => 'Needs you' },
  awaiting_response: { tone: 'held',    label: (d) => withDays('With them', d) },
  in_progress:       { tone: 'held',    label: (d) => withDays('With them', d) },
  data_received:     { tone: 'done',    label: () => 'Records received' },
  no_sar_needed:     { tone: 'done',    label: () => 'Already have records' },
};

// Kept for callers that only need a label (e.g. filters / legends).
export const SAR_STATUS_CONFIG = Object.fromEntries(
  Object.entries(SAR_TONES).map(([k, v]) => [k, { label: v.label(), tone: v.tone }]),
);
export const ORG_STATUS_CONFIG = Object.fromEntries(
  Object.entries(ORG_TONES).map(([k, v]) => [k, { label: v.label(), tone: v.tone }]),
);

export const StatusPill = ({ tone = 'neutral', children, size = 'md', className = '', title, testId }) => {
  const t = TONES[tone] || TONES.neutral;
  const sizing = size === 'sm' ? 'px-[10px] py-[3px] text-[13px] gap-[6px]' : 'px-[13px] py-[6px] text-sm gap-[7px]';
  const dot = size === 'sm' ? 'w-[6px] h-[6px]' : 'w-[7px] h-[7px]';
  return (
    <span
      className={`inline-flex items-center rounded-full font-medium whitespace-nowrap ${sizing} ${t.chip} ${className}`}
      title={title}
      data-testid={testId}
    >
      <span aria-hidden="true" className={`rounded-full shrink-0 ${dot} ${t.dot}`} />
      {children}
    </span>
  );
};

/**
 * Organisation status chip. `reason` becomes the accessible title (hover) —
 * it is a sentence about *why*, not the state itself, so it needn't be visible.
 */
export const OrgStatusPill = ({ status, reason, daysRemaining, size = 'md' }) => {
  const entry = ORG_TONES[status];
  if (!entry) return null;
  return (
    <StatusPill tone={entry.tone} size={size} title={reason || undefined} testId={`org-status-${status}`}>
      {entry.label(daysRemaining)}
    </StatusPill>
  );
};

/** SAR status chip. The day count is in the label, never hidden in a tooltip. */
export const SARStatusPill = ({ status, daysRemaining, size = 'md' }) => {
  const entry = SAR_TONES[status];
  if (!entry) return null;
  return (
    <StatusPill tone={entry.tone} size={size} testId={`sar-status-${status}`}>
      {entry.label(daysRemaining)}
    </StatusPill>
  );
};

export default { OrgStatusPill, SARStatusPill, StatusPill };
